import React from "react";
import { StyleSheet, View } from "react-native";
import { MediaModel } from "common247";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import MediaGallery from "./MediaGallery";
import { ThemedButton } from "@/components/ThemedButton";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

/**
 * Props for the MediaSelector popup component.
 */
export interface IMediaSelectorProps {
  /** Whether the media selector popup is currently visible */
  visible: boolean;
  /** Callback fired when the selector is dismissed */
  onClose: () => void;
  /** Optional contextual coordinates of cursor or trigger element */
  anchorPosition?: IPopoverAnchor | null;
  /** Initially or currently selected media IDs */
  selectedIds?: string[];
  /** Callback invoked when the user selects or deselects media items */
  onSelectMedia: (media: MediaModel[]) => void;
  /** Whether multiple media items can be selected (default: true) */
  multiSelect?: boolean;
  /** Callback fired when a single media item is selected (in single-select mode) */
  onSelectSingleMedia?: (media: MediaModel) => void;
  /** Optional title override (default: "Select Media") */
  title?: string;
  /** Optional subtitle or instructions */
  subtitle?: string;
  /** Target width for the popover card (default: 480) */
  width?: number;
  /** Target maximum height for the popover card (default: 520) */
  maxHeight?: number;
  /** Optional testID for testing */
  testID?: string;
  /** Optional filter to restrict media types (e.g. ['image']). When provided, only matching media is displayed. */
  allowedMediaTypes?: string[];
  /** Convenience shortcut to restrict the selector to images only */
  imagesOnly?: boolean;
}

const DEFAULT_EMPTY_IDS: string[] = [];

/**
 * Contextual MediaSelector powered by ThemedPopupScreen.
 * Provides searchable media grid selection anchored contextually to the user cursor/trigger.
 * Supports both multi-select (default) and single-select modes.
 */
export function MediaSelector({
  visible,
  onClose,
  anchorPosition,
  selectedIds = DEFAULT_EMPTY_IDS,
  onSelectMedia,
  multiSelect = true,
  onSelectSingleMedia,
  title = "Select Media",
  subtitle,
  width = 480,
  maxHeight = 520,
  testID = "media-selector",
  allowedMediaTypes,
  imagesOnly = false,
}: IMediaSelectorProps) {
  const effectiveAllowedTypes = imagesOnly ? ["image"] : allowedMediaTypes;

  const handleSelectMedia = (items: MediaModel[]) => {
    if (!multiSelect) {
      if (items.length > 0) {
        const item = items[items.length - 1];
        onSelectSingleMedia?.(item);
        onSelectMedia([item]);
        onClose();
      }
      return;
    }
    onSelectMedia(items);
  };

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={title}
      subtitle={subtitle}
      icon="images-outline"
      width={width}
      maxHeight={maxHeight}
      testID={testID}
      footer={
        <View style={styles.footerRow}>
          <ThemedButton
            title="Cancel"
            variant="secondary"
            onPress={onClose}
          />
          {multiSelect && (
            <ThemedButton
              title="Done"
              variant="primary"
              compact
              onPress={onClose}
              testID={`${testID}-done-btn`}
            />
          )}
        </View>
      }
    >
      <View style={styles.galleryWrapper}>
        <MediaGallery
          selectionMode={true}
          viewMode="compact"
          showViewSelector={false}
          initialSelectedIds={selectedIds}
          onSelect={handleSelectMedia}
          allowedMediaTypes={effectiveAllowedTypes}
        />
      </View>
    </ThemedPopupScreen>
  );
}

export default MediaSelector;

const styles = StyleSheet.create({
  galleryWrapper: {
    flex: 1,
    minHeight: 280,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
  },
});
